import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck } from 'lucide-react';
import CountBadge from '../ui/CountBadge';
import { findNavEntryByPath } from '../layout/navigationModules';
import { useNavConfig } from '../../api/Navigation/moduleConfig';
import { usePendingActionsCount } from './usePendingActions';

const PENDING_ACTIONS_PATH = '/pending-actions';

/**
 * TopBar entry: a pending-actions count next to the notifications bell.
 * Renders nothing while the user has nothing pending — no dead chrome.
 * The bubble is the shared CountBadge, the same one the bell uses (CO-873),
 * which also supplies the box and the 99+ cap. The button's name carries
 * the exact count for screen readers.
 *
 * The badge is a link to /pending-actions, which the `home` module owns and which is switchable per
 * company. When that page or its module is off, PageConfigGuard blocks the destination and bounces
 * the user, so the badge is withheld rather than offered as a link that silently goes nowhere. Same
 * rule the sidebar and the guard apply (page row AND every owning module id).
 */
const TopBarPendingActions: React.FC = () => {
  const { t } = useTranslation('requests');
  const navigate = useNavigate();
  const { count } = usePendingActionsCount();
  const navConfig = useNavConfig();

  const entry = findNavEntryByPath(PENDING_ACTIONS_PATH);
  const pageEnabled =
    entry === undefined ||
    navConfig.isPageEnabled(entry.pagePath, [entry.moduleId, ...(entry.subModuleId ? [entry.subModuleId] : [])]);

  if (count <= 0 || !pageEnabled) return null;

  return (
    <button
      type="button"
      onClick={() => navigate(PENDING_ACTIONS_PATH)}
      aria-label={t('{{n}} pending actions', { n: count })}
      title={t('Pending actions')}
      data-testid="pa-topbar-badge"
      className="rounded-lg transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <CountBadge count={count}>
        <ClipboardCheck size={20} className="text-muted-foreground" aria-hidden="true" />
      </CountBadge>
    </button>
  );
};

export default TopBarPendingActions;
